const LANGUAGES = ['en', 'hi', 'mr', 'gu'];

/** A base64 image rather than a link to one. Megabytes, not bytes. */
function isDataUrl(value) {
  return typeof value === 'string' && value.startsWith('data:');
}

class GmImagesState {
  constructor() {
    this.state = this.initializeState();
    this.listeners = [];
  }

  initializeState() {
    const state = {};
    LANGUAGES.forEach(lang => {
      state[lang] = {
        file: null,
        preview: null,
        existingUrl: null,
        storagePath: null,
        uploadedAt: null
      };
    });
    return state;
  }

  /**
   * Is there a chosen image for this language that is not in the database yet?
   *
   * `preview` alone does not mean pending: both loading a stored image and
   * finishing an upload set it to the download URL, so anything keyed off it
   * called every saved image unsaved. Pending is a local file, or a preview
   * with no stored URL behind it.
   */
  hasPendingUpload(lang) {
    const state = this.state[lang];
    if (!state) return false;
    if (state.file) return true;
    return !!state.preview && !state.existingUrl;
  }

  getState(lang = null) {
    if (lang) {
      return this.state[lang];
    }
    return this.state;
  }

  setState(lang, updates) {
    this.state[lang] = { ...this.state[lang], ...updates };
    this.notifyListeners(lang);
    this.saveToLocalStorage();
  }

  resetState(lang = null) {
    if (lang) {
      this.state[lang] = {
        file: null,
        preview: null,
        existingUrl: null,
        storagePath: null,
        uploadedAt: null
      };
      this.notifyListeners(lang);
    } else {
      this.state = this.initializeState();
      this.notifyListeners();
    }
    this.saveToLocalStorage();
  }

  /** The date this cached state belongs to. */
  setDate(dateString) {
    this.currentDate = dateString || null;
    this.saveToLocalStorage();
  }

  /**
   * Cache the slot references for this date.
   *
   * Deliberately never stores a `data:` preview. Those are whole images in
   * base64 — four of them overflowed the ~5MB origin quota, and because the
   * quota error aborts the whole write, the small Storage references were lost
   * along with them: staging one image silently disabled the entire cache.
   *
   * Nothing is lost by dropping them. The File behind a staged preview is not
   * persistable either, so a restored data URL was only ever half the state.
   */
  saveToLocalStorage() {
    // Stamp the date. The cache used to be a single undated key, so
    // previews staged for one date were restored onto another — and an
    // un-uploaded preview is uploaded to whatever date is open
    // (gmImagesController: uploadFromDataURL), putting the wrong image
    // on the wrong day.
    const stateToSave = { __date: this.currentDate || null };
    LANGUAGES.forEach(lang => {
      const { preview, existingUrl, uploadedAt, storagePath } = this.state[lang];
      stateToSave[lang] = {
        preview: isDataUrl(preview) ? null : preview,
        existingUrl,
        uploadedAt,
        storagePath
      };
    });

    try {
      localStorage.setItem('gmImagesState', JSON.stringify(stateToSave));
    } catch (error) {
      console.warn('Failed to save GM images state to localStorage:', error);
    }
  }

  loadFromLocalStorage(expectedDate = null) {
    try {
      const savedState = localStorage.getItem('gmImagesState');
      if (savedState) {
        const parsed = JSON.parse(savedState);

        // Cached state belongs to one date only. Anything else is discarded
        // rather than shown against the date now open.
        if (expectedDate && parsed.__date && parsed.__date !== expectedDate) {
          this.clearLocalStorage();
          return false;
        }

        LANGUAGES.forEach(lang => {
          if (parsed[lang]) {
            this.state[lang] = {
              ...this.state[lang],
              preview: parsed[lang].preview || null,
              existingUrl: parsed[lang].existingUrl || null,
              uploadedAt: parsed[lang].uploadedAt || null,
              storagePath: parsed[lang].storagePath || null
            };
          }
        });
        this.notifyListeners();
        return true;
      }
      return false;
    } catch (error) {
      console.warn('Failed to load GM images state from localStorage:', error);
      return false;
    }
  }

  clearLocalStorage() {
    try {
      localStorage.removeItem('gmImagesState');
    } catch (error) {
      console.warn('Failed to clear GM images state from localStorage:', error);
    }
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners(lang = null) {
    this.listeners.forEach(listener => listener(this.state, lang));
  }

  hasFile(lang) {
    return this.state[lang].file !== null;
  }

  hasPreview(lang) {
    return this.state[lang].preview !== null;
  }

  isUploaded(lang) {
    return this.state[lang].existingUrl !== null;
  }

  getUploadedCount() {
    return LANGUAGES.filter(lang => this.isUploaded(lang)).length;
  }

  getUploadedLanguages() {
    return LANGUAGES.filter(lang => this.isUploaded(lang));
  }

  getMissingLanguages() {
    return LANGUAGES.filter(lang => !this.isUploaded(lang));
  }

  /** Languages holding a picked-but-not-yet-uploaded file. */
  getStagedLanguages() {
    return LANGUAGES.filter(lang => this.hasFile(lang));
  }
}

export const gmImagesState = new GmImagesState();
export { LANGUAGES };
